import { Stack } from "@mui/material";
import { useCallback, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { WorkflowSignalType } from "@entrilia/shared/api/workflowTypes";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../api/adminApi";
import type { ObjectWorkflowMetadata } from "../../../api/types/objectTypes";
import type { NextWorkflowApproval, WorkflowApproval } from "../../../api/types/workflowTypes";
import ObjectWorkflowApproversAssignmentDialog from "./ObjectWorkflowApproversAssignmentDialog";
import WorkflowActionConfirmationDialog from "./WorkflowActionConfirmationDialog";
import WorkflowSignalActionButton from "./WorkflowSignalActionButton";

interface Props {
  objectTypeName: string;
  objectName: string;
  objectType: string;
  objectId: string;
  workflowMetadata: ObjectWorkflowMetadata;
  actionsWithChecks?: WorkflowSignalType[];
  hasUnsavedEdits: boolean;
  onActionExecuted: (signalType: WorkflowSignalType) => void;
}

interface DialogState {
  signalToExecute: WorkflowSignalType;
  manualWorkflowApproval?: WorkflowApproval;
}

const getNextWorkflowApproval = withErrorHandling(adminApi.getNextWorkflowApproval);

const getWorkflowApprovalForManualAssignment = (nextApproval: NextWorkflowApproval) =>
  nextApproval.approversAssignmentMode === "Manual" ? nextApproval.approvalPreview : undefined;

const ObjectWorkflowActions = ({
  objectTypeName,
  objectName,
  objectType,
  objectId,
  workflowMetadata,
  actionsWithChecks,
  hasUnsavedEdits,
  onActionExecuted,
}: Props) => {
  const { sendNotificationError } = useNotificationContext();

  const [dialogState, setDialogState] = useState<DialogState>();
  const [loadingForSignal, setLoadingForSignal] = useState<WorkflowSignalType>();

  const getPreloadedNextApproval = useCallback(
    () => adminApi.getNextWorkflowApproval(workflowMetadata.workflowId),
    [workflowMetadata]
  );

  const [preloadedNextApproval, preloadError, { isFetching: isPreloadingNextApproval }] = useFetch(
    getPreloadedNextApproval,
    { disabled: !workflowMetadata.availableUserSignals.includes("SubmitInvoiceForApproval") }
  );

  if (preloadError) {
    logError(preloadError, "[ObjectWorkflowActions] preload getNextWorkflowApproval");
  }

  const isBlockedByUnsavedEdits = (signal: WorkflowSignalType) =>
    hasUnsavedEdits && actionsWithChecks?.includes(signal) === true;

  // Card edits commit on click-away, after the click that opened the dialog
  if (dialogState !== undefined && isBlockedByUnsavedEdits(dialogState.signalToExecute)) {
    setDialogState(undefined);
  }

  const handleActionClick = async (signal: WorkflowSignalType) => {
    if (signal !== "SubmitInvoiceForApproval") {
      setDialogState({ signalToExecute: signal });
      return;
    }

    if (preloadedNextApproval && !preloadError && !isPreloadingNextApproval) {
      setDialogState({
        signalToExecute: signal,
        manualWorkflowApproval: getWorkflowApprovalForManualAssignment(preloadedNextApproval),
      });
      return;
    }

    setLoadingForSignal(signal);
    const [resp, error] = await getNextWorkflowApproval(workflowMetadata.workflowId);
    setLoadingForSignal(undefined);
    if (error) {
      logError(error, "[ObjectWorkflowActions] getWorkflowApprovals");
      sendNotificationError(error.message);
      return;
    }

    setDialogState({
      signalToExecute: signal,
      manualWorkflowApproval: getWorkflowApprovalForManualAssignment(resp),
    });
  };

  return (
    <>
      <Stack direction="row" spacing={1}>
        {workflowMetadata.availableUserSignals.map((signal) => (
          <WorkflowSignalActionButton
            key={signal}
            signalType={signal}
            loading={loadingForSignal === signal}
            disabled={loadingForSignal !== undefined || isBlockedByUnsavedEdits(signal)}
            disabledReason={
              isBlockedByUnsavedEdits(signal) ? "Save or cancel your changes before proceeding." : undefined
            }
            onClick={handleActionClick}
          />
        ))}
      </Stack>

      {dialogState !== undefined && dialogState.manualWorkflowApproval === undefined && (
        <WorkflowActionConfirmationDialog
          workflowId={workflowMetadata.workflowId}
          signalType={dialogState.signalToExecute}
          objectTypeName={objectTypeName}
          objectName={objectName}
          objectType={objectType}
          objectId={objectId}
          showActionChecks={actionsWithChecks?.includes(dialogState.signalToExecute)}
          onClose={() => setDialogState(undefined)}
          onActionExecuted={onActionExecuted}
        />
      )}

      {dialogState !== undefined && dialogState.manualWorkflowApproval !== undefined && (
        <ObjectWorkflowApproversAssignmentDialog
          workflowId={workflowMetadata.workflowId}
          signalType={dialogState.signalToExecute}
          approvalPreview={dialogState.manualWorkflowApproval}
          objectType={objectType}
          objectId={objectId}
          showActionChecks={actionsWithChecks?.includes(dialogState.signalToExecute)}
          onClose={() => setDialogState(undefined)}
          onActionExecuted={onActionExecuted}
        />
      )}
    </>
  );
};

export default ObjectWorkflowActions;
